// MultiPaneChart: candlestick chart with volume/RSI/MACD panes, backend trading
// analysis (entry/TP/SL markers), swing-point overlay, and prediction band.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, AreaSeries, createSeriesMarkers } from 'lightweight-charts';
import TechnicalIndicatorsService from '../../services/TechnicalIndicatorsService';
import { fetchTradingAnalysis, fetchPrediction } from '../../services/api';
import TimeframeControls from './TimeframeControls';
import IndicatorControls from './IndicatorControls';
import PredictionBandSeries from './PredictionBandSeries';
import { DEFAULT_SUPERTREND_CONFIG } from './defaultSuperTrendConfig';
import InfoHint from '../Common/InfoHint';
import { detectSwingPatterns } from '../../utils/swingPatterns';
import { buildAnalysisMarkers } from '../../utils/analysisMarkers';
import SwingProfilePrimitive from './SwingProfilePrimitive';
import { computeSwingProfiles } from '../../utils/swingProfile';

const DEBUG = process.env.REACT_APP_DEBUG === 'true';

// Overlay scale margins for the in-chart indicator stack. Regions stack from
// the bottom UP: volume (bottom), then RSI, then MACD; price fills the top. A
// GAP separates each so they never touch. Fractions are of the whole pane
// height (0 = top, 1 = bottom). Shared by chart build + the toggle effect so
// enabling MACD/RSI never rebuilds the chart (they're client-side only).
function computePaneMargins({ showVolume, showRSI, showMACD }) {
  const GAP = 0.025;
  const rsiFrac = showRSI ? 0.12 : 0;
  const macdFrac = showMACD ? 0.13 : 0;
  const volFrac = showVolume ? 0.10 : 0;
  let cursor = 0; // distance from the bottom edge
  const volume = { top: 1 - volFrac, bottom: 0 };
  if (showVolume) cursor += volFrac + GAP;
  const rsi = { top: 1 - cursor - rsiFrac, bottom: cursor };
  if (showRSI) cursor += rsiFrac + GAP;
  const macd = { top: 1 - cursor - macdFrac, bottom: cursor };
  if (showMACD) cursor += macdFrac + GAP;
  const price = { top: 0.03, bottom: cursor };
  return { price, volume, rsi, macd };
}

const MultiPaneChart = ({
  marketData, 
  symbol = 'VCB.VN',
  height = 1000,
  showVolume = true,
  showRSI = false,
  showMACD = false,
  indicatorConfig = 'advanced',
  // NEW: SuperTrend Pro v2 props
  showSuperTrend = false, // This prop now enables the whole integrated system
  superTrendConfig = {}, // This will now configure the system
  onNewAnalysis = null, // A new callback for the full result
  showRegimeBackground = true, // <-- ADD THIS NEW PROP
  // Timeframe is fully controlled by the parent: this component renders the buttons
  // and reports clicks via onTimeframeChange, but the single source of truth is
  // App's selectedTimeframe (duplicating it as local state caused drift bugs).
  timeframe = '1D',
  onTimeframeChange = () => {},
  // Auth gating: logged-out visitors see the chart + trend line + manual
  // indicators, but NOT the entry/TP/SL signals or the Matching projection.
  isAuthed = true,
  onRequestLogin = () => {},
}) => {
  // Debug incoming props
  if (DEBUG) {
    console.log('🔍 MultiPaneChart props:', {
      hasMarketData: !!marketData,
      symbol,
      marketDataKeys: marketData ? Object.keys(marketData) : 'none',
      candlesCount: marketData?.candles?.length || 0,
      showSuperTrend
    });
  }

  // ADD a single state for the new, comprehensive analysis result
  const [analysisResult, setAnalysisResult] = useState(null);
  const [forecastSummary, setForecastSummary] = useState(null);
  const [viewingMatch, setViewingMatch] = useState(false);

  // A new symbol/timeframe invalidates the matching line — hide the chip until
  // the fresh prediction arrives so it can never point at a stale line. Also
  // clear the purple match flags and return to the live edge: without this,
  // switching timeframe while VIEWING a match stranded the user in the past
  // with no "back to now" button (the chip that owns it was just hidden).
  // Critically, also blank the stale analysis-derived series NOW: otherwise the
  // old symbol's SuperTrend/ION line (e.g. VCB ~61,752) keeps painting on the
  // new symbol's chart (e.g. AAPL ~200) until fresh analysis lands, blowing out
  // the price scale and squishing the real candles — worse still if the new
  // symbol's analysis is slow or fails. We DON'T null analysisResult (that
  // races the incoming fetch and the line never repaints); instead the marker
  // effect skips any result whose symbol/timeframe tag doesn't match the
  // current one, so stale data can't paint while fresh data still does.
  useEffect(() => {
    setForecastSummary(null);
    setViewingMatch(false);
    tooltipPinnedRef.current = false;
    setMarkerTooltip(null);
    if (seriesRef.current.superTrend) seriesRef.current.superTrend.setData([]);
    if (seriesRef.current.candlestickMarkers) seriesRef.current.candlestickMarkers.setMarkers([]);
    if (seriesRef.current.prediction) seriesRef.current.prediction.setData([]);
    if (seriesRef.current.predictionBand) seriesRef.current.predictionBand.setData([]);
    if (seriesRef.current.predictionHigh) seriesRef.current.predictionHigh.setData([]);
    if (seriesRef.current.predictionLow) seriesRef.current.predictionLow.setData([]);
    if (seriesRef.current.matchMarkers) seriesRef.current.matchMarkers.setMarkers([]);
    chartsRef.current.main?.timeScale().scrollToRealTime();
  }, [symbol, timeframe]);
  
  // Marker tooltip state remains the same
  const [markerTooltip, setMarkerTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  // Pinned = opened by a TAP/CLICK (mobile has no hover). While pinned, crosshair
  // hover won't override or clear it; a tap on empty chart unpins it.
  const tooltipPinnedRef = useRef(false);

  // Keep the tooltip fully inside the chart container (crucial on phone, where
  // it would otherwise render off the right/bottom edge).
  const clampTooltip = useCallback((x, y) => {
    const c = containerRef.current;
    const W = c ? c.clientWidth : 360;
    const H = c ? c.clientHeight : 400;
    const TW = 256; // matches w-64
    const TH = 190; // approx tooltip height
    let left = x + 14;
    if (left + TW > W - 8) left = x - TW - 14;      // flip to the left of the point
    left = Math.max(8, Math.min(left, W - TW - 8));
    let top = y + 14;
    top = Math.max(8, Math.min(top, Math.max(8, H - TH - 8)));
    return { x: left, y: top };
  }, []);

  // Defaults live in defaultSuperTrendConfig.js; the caller's overrides win.
  // JSON.stringify keeps the memo stable when the caller passes a fresh-but-equal
  // object literal each render.
  const stableSuperTrendConfig = useMemo(() => ({
    ...DEFAULT_SUPERTREND_CONFIG,
    ...superTrendConfig
  }), [JSON.stringify(superTrendConfig)]);

  const hasMarketData = Boolean(marketData && marketData.candles && marketData.candles.length > 0);
  const effectiveMarketData = marketData;

  const containerRef = useRef(null);
  const chartsRef = useRef({});
  const seriesRef = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [candleDataRef, setCandleDataRef] = useState([]);
  const [currentIndicatorConfig, setCurrentIndicatorConfig] = useState(
    typeof indicatorConfig === 'object' 
      ? indicatorConfig 
      : TechnicalIndicatorsService.getPresets()[indicatorConfig] || TechnicalIndicatorsService.getPresets()['advanced']
  );
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Pane visibility lives in the indicator config (toggleable from the pill
  // bar); the showRSI/showMACD props remain only as initial defaults.
  const effectiveShowRSI = currentIndicatorConfig?.rsiPane?.enabled ?? showRSI;
  const effectiveShowMACD = currentIndicatorConfig?.macdPane?.enabled ?? showMACD;
  const analysisRequestIdRef = useRef(0);
  const fullAnalysisTimeoutRef = useRef(null);
  const predictionTimeoutRef = useRef(null);
  const lastAnalysisBarKeyRef = useRef(null);

  // FIXED: Stable indicator configuration getter
  const getIndicatorConfig = useCallback(() => {
    return currentIndicatorConfig;
  }, [currentIndicatorConfig]);

  // Add/remove indicator line series to match the config. Data for these series is
  // calculated and set by the indicator-update effect (single owner) — this only
  // manages which series exist on the chart.
  const syncIndicatorSeries = useCallback((config) => {
    if (!chartsRef.current.main) return;

    // Helper to add/remove series
    const manageSeries = (seriesGroup, seriesKey, condition, seriesOptions) => {
      if (condition) {
        if (!seriesGroup[seriesKey]) {
          seriesGroup[seriesKey] = chartsRef.current.main.addSeries(LineSeries, seriesOptions);
        }
      } else if (seriesGroup[seriesKey]) {
        chartsRef.current.main.removeSeries(seriesGroup[seriesKey]);
        delete seriesGroup[seriesKey];
      }
    };
    
    // Manage SMA series
    if (seriesRef.current.smaLines) {
      const nextSmaPeriods = new Set((config.sma?.periods || []).map(p => Number(p)));
      Object.keys(seriesRef.current.smaLines).forEach(key => {
        const period = Number(key);
        if (!config.sma?.enabled || !nextSmaPeriods.has(period)) {
          chartsRef.current.main.removeSeries(seriesRef.current.smaLines[key]);
          delete seriesRef.current.smaLines[key];
        }
      });
    }
    if (config.sma?.periods) {
      config.sma.periods.forEach((period, index) => {
        const colors = ['#2962FF', '#FF6B35', '#9C27B0', '#4CAF50', '#FF9800'];
        manageSeries(seriesRef.current.smaLines, period, config.sma.enabled, {
          color: colors[index] || '#666666',
          lineWidth: 2,
          title: `SMA${period}`,
        });
      });
    }

    // Manage EMA series
    if (seriesRef.current.emaLines) {
      const nextEmaPeriods = new Set((config.ema?.periods || []).map(p => Number(p)));
      Object.keys(seriesRef.current.emaLines).forEach(key => {
        const period = Number(key);
        if (!config.ema?.enabled || !nextEmaPeriods.has(period)) {
          chartsRef.current.main.removeSeries(seriesRef.current.emaLines[key]);
          delete seriesRef.current.emaLines[key];
        }
      });
    }
    if (config.ema?.periods) {
      config.ema.periods.forEach((period, index) => {
        const colors = ['#00BCD4', '#E91E63', '#795548', '#607D8B'];
        manageSeries(seriesRef.current.emaLines, period, config.ema.enabled, {
          color: colors[index] || '#999999',
          lineWidth: 2,
          title: `EMA${period}`,
        });
      });
    }

    // Manage Bollinger Bands
    const bbCondition = config.bollingerBands?.enabled;
    manageSeries(seriesRef.current.bollingerBands, 'upper', bbCondition, { color: '#9E9E9E', lineWidth: 1, title: 'BB Upper' });
    manageSeries(seriesRef.current.bollingerBands, 'middle', bbCondition, { color: '#9E9E9E', lineWidth: 1, title: 'BB Middle' });
    manageSeries(seriesRef.current.bollingerBands, 'lower', bbCondition, { color: '#9E9E9E', lineWidth: 1, title: 'BB Lower' });

    // Manage VWAP
    manageSeries(seriesRef.current, 'vwap', config.vwap?.enabled, { color: '#FFC107', lineWidth: 2, title: 'VWAP' });
  }, []);

  // Handle indicator configuration changes: sync which series exist, then update the
  // config state — the indicator-update effect recalculates data for the new set.
  const handleIndicatorConfigChange = useCallback((newConfig) => {
    if (DEBUG) {
      console.log('🔧 Indicator config changed:', newConfig);
    }
    syncIndicatorSeries(newConfig);
    setCurrentIndicatorConfig(newConfig);
  }, [syncIndicatorSeries]);

  // FIXED: Stable crosshair move handler (desktop hover). A pinned (tapped)
  // tooltip takes precedence — hover must not clear or move it.
  const handleCrosshairMove = useCallback((param) => {
    if (tooltipPinnedRef.current) return;
    const markers = seriesRef.current.currentMarkers || [];

    if (!param || !param.point) {
      setMarkerTooltip(current => current ? null : current);
      return;
    }

    const hovered = markers.find(m => m.time === param.time);

    if (hovered) {
      setMarkerTooltip(current => {
        if (!current || current.id !== hovered.id) {
          setTooltipPosition(clampTooltip(param.point.x, param.point.y));
          return hovered;
        }
        return current;
      });
    } else {
      setMarkerTooltip(current => current ? null : current);
    }
  }, [clampTooltip]);

  // Tap/click handler — the ONLY way to read a signal's reason on touch. Finds
  // the nearest marker to the tapped bar (within a few bars) and pins its
  // tooltip; a tap away from any marker dismisses it. Subscribed via a ref
  // wrapper so it always sees the latest candle data without re-subscribing.
  const handleChartClick = useCallback((param) => {
    const markers = seriesRef.current.currentMarkers || [];
    if (!param || param.time == null || markers.length === 0) {
      tooltipPinnedRef.current = false;
      setMarkerTooltip(null);
      return;
    }
    let best = null;
    let bestDiff = Infinity;
    for (const m of markers) {
      const d = Math.abs(m.time - param.time);
      if (d < bestDiff) { best = m; bestDiff = d; }
    }
    const cd = candleDataRef;
    const interval = (cd && cd.length > 1) ? Math.abs(cd[1].time - cd[0].time) || 86400 : 86400;
    if (best && bestDiff <= interval * 2.5) {
      tooltipPinnedRef.current = true;
      setMarkerTooltip(best);
      const pt = param.point || { x: 40, y: 40 };
      setTooltipPosition(clampTooltip(pt.x, pt.y));
    } else {
      tooltipPinnedRef.current = false;
      setMarkerTooltip(null);
    }
  }, [candleDataRef, clampTooltip]);

  // stable subscription wrapper that always calls the latest click handler
  const clickHandlerRef = useRef(() => {});
  clickHandlerRef.current = handleChartClick;

  // FIXED: Initialize chart with v5 panes - STABLE DEPENDENCIES
  useEffect(() => {
    if (DEBUG) {
      console.log('🏗️ Initializing v5 chart with panes and SuperTrend Pro v2 support');
    }
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = '';
    
    // FIXED: Chart options for v5 with proper interaction settings
    const chartOptions = {
      layout: {
        background: { type: 'solid', color: '#ffffff' },
        textColor: '#333333',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        barSpacing: 6,
        minBarSpacing: 0.5,
      },
      rightPriceScale: {
        width: 80,
        borderColor: '#e5e7eb',
        visible: true,
        autoScale: true,
      },
      crosshair: {
        mode: 1, // FIXED: Use normal crosshair mode
        vertLine: {
          width: 1,
          color: '#9B7DFF',
          style: 0,
          labelVisible: true,
        },
        horzLine: {
          width: 1,
          color: '#9B7DFF',
          style: 0,
          labelVisible: true,
        },
      },
      width: container.clientWidth,
      height: container.clientHeight || height,
      // FIXED: Proper mouse interactions
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
      watermark: {
        visible: true,
        fontSize: 24,
        color: 'rgba(0, 0, 0, 0.1)',
        text: symbol,
        fontFamily: 'Roboto, sans-serif',
        fontStyle: 'normal',
        horzAlign: 'right',
        vertAlign: 'bottom',
      },
    };

    // Create single chart with multiple panes
    const chart = createChart(container, chartOptions);
    chartsRef.current.main = chart;

    // FIXED: Use proper imported series types and correct API
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    seriesRef.current.candlestick = candlestickSeries;
    seriesRef.current.candlestickMarkers = createSeriesMarkers(candlestickSeries, []); // Create markers instance
    // Separate, independently-toggleable primitive so swing/pattern labels never
    // interfere with entry/TP/SL markers (each primitive owns its own marker set).
    seriesRef.current.swingPatternMarkers = createSeriesMarkers(candlestickSeries, []);
    // Purple flags marking the matched historical window when the user jumps to it
    seriesRef.current.matchMarkers = createSeriesMarkers(candlestickSeries, []);

    // Swing volume profile (S/R zones + Point of Control), fed by its own effect
    const swingProfilePrimitive = new SwingProfilePrimitive();
    candlestickSeries.attachPrimitive(swingProfilePrimitive);
    seriesRef.current.swingProfile = swingProfilePrimitive;

    // Prediction band between high/low
    const predictionBand = chart.addCustomSeries(new PredictionBandSeries(), {
      color: 'rgba(0, 0, 0, 0)',
      fillColor: 'rgba(59, 130, 246, 0.18)',
    });

    // Prediction line only (no fill)
    const predictionArea = chart.addSeries(AreaSeries, {
      lineColor: '#1d4ed8', // bold blue
      topColor: 'rgba(0,0,0,0)',
      bottomColor: 'rgba(0,0,0,0)',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'Matching',
    });
    const predictionSeries = predictionArea;
    const predictionHigh = chart.addSeries(LineSeries, {
      color: '#93c5fd',
      lineWidth: 1,
      lineStyle: 2,
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const predictionLow = chart.addSeries(LineSeries, {
      color: '#bfdbfe',
      lineWidth: 1,
      lineStyle: 2,
      title: '',
      priceLineVisible: false,
      lastValueVisible: false,
    });
    seriesRef.current.prediction = predictionSeries;
    seriesRef.current.predictionBand = predictionBand;
    seriesRef.current.predictionHigh = predictionHigh;
    seriesRef.current.predictionLow = predictionLow;

    // FIXED: Subscribe crosshair move with stable handler
    //view information of label
    chart.subscribeCrosshairMove(handleCrosshairMove);
    // Tap/click to pin a signal's reason tooltip (mobile has no hover).
    chart.subscribeClick((param) => clickHandlerRef.current(param));

    // NEW: Add SuperTrend Pro v2 line if enabled
    if (showSuperTrend) {
      const superTrendLine = chart.addSeries(LineSeries, {
        color: '#26a69a',
        lineWidth: 3,
        title: 'ION',
        lineStyle: 0,
        crosshairMarkerVisible: true,
      });
      
      seriesRef.current.superTrend = superTrendLine;

      // Add market structure EMAs if enabled in SuperTrend config
      if (stableSuperTrendConfig.useMarketStructure) {
        seriesRef.current.superTrendEmaFast = chart.addSeries(LineSeries, {
          color: '#ffeb3b',
          lineWidth: 1,
          title: `EMA ${stableSuperTrendConfig.emaFast}`,
        });

        seriesRef.current.superTrendEmaSlow = chart.addSeries(LineSeries, {
          color: '#ff9800',
          lineWidth: 1,
          title: `EMA ${stableSuperTrendConfig.emaSlow}`,
        });

        seriesRef.current.superTrendEmaBaseline = chart.addSeries(LineSeries, {
          color: '#9c27b0',
          lineWidth: 2,
          title: `EMA ${stableSuperTrendConfig.emaBaseline}`,
        });
      }
    }

    // Lower indicator regions live INSIDE the single main pane as overlay price
    // scales (no extra panes — total chart height stays fixed, price stays big).
    // Volume is created here; MACD/RSI are added/removed by a dedicated toggle
    // effect below, so enabling them never rebuilds the chart or refetches the
    // signals/matching line (they're 100% client-side).
    const initialMargins = computePaneMargins({
      showVolume, showRSI: effectiveShowRSI, showMACD: effectiveShowMACD,
    });
    chart.priceScale('right').applyOptions({ scaleMargins: initialMargins.price });

    if (showVolume) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: '#26a69a',
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume_overlay',
        lastValueVisible: true,
        priceLineVisible: false,
      });
      volumeSeries.priceScale().applyOptions({ scaleMargins: initialMargins.volume });
      seriesRef.current.volume = volumeSeries;
    }

    // Add technical indicators to main pane
    const config = getIndicatorConfig();
    const smaLines = {};
    const emaLines = {};
    
    if (config.sma?.enabled && config.sma?.periods) {
      config.sma.periods.forEach((period, index) => {
        const colors = ['#2962FF', '#FF6B35', '#9C27B0', '#4CAF50', '#FF9800'];
        smaLines[period] = chart.addSeries(LineSeries, {
          color: colors[index] || '#666666',
          lineWidth: 2,
          title: `SMA${period}`,
        });
      });
    }

    if (config.ema?.enabled && config.ema?.periods) {
      config.ema.periods.forEach((period, index) => {
        const colors = ['#00BCD4', '#E91E63', '#795548', '#607D8B'];
        emaLines[period] = chart.addSeries(LineSeries, {
          color: colors[index] || '#999999',
          lineWidth: 2,
          title: `EMA${period}`,
        });
      });
    }

    // Add Bollinger Bands if enabled
    let bollingerBands = {};
    if (config.bollingerBands?.enabled) {
      bollingerBands.upper = chart.addSeries(LineSeries, {
        color: '#9E9E9E',
        lineWidth: 1,
        title: 'BB Upper',
      });

      bollingerBands.middle = chart.addSeries(LineSeries, {
        color: '#9E9E9E',
        lineWidth: 1,
        title: 'BB Middle',
      });

      bollingerBands.lower = chart.addSeries(LineSeries, {
        color: '#9E9E9E',
        lineWidth: 1,
        title: 'BB Lower',
      });
    }

    // Add VWAP if enabled
    let vwapSeries = null;
    if (config.vwap?.enabled) {
      vwapSeries = chart.addSeries(LineSeries, {
        color: '#FFC107',
        lineWidth: 2,
        title: 'VWAP',
      });
    }

    seriesRef.current.smaLines = smaLines;
    seriesRef.current.emaLines = emaLines;
    seriesRef.current.bollingerBands = bollingerBands;
    seriesRef.current.vwap = vwapSeries;

    // (MACD & RSI overlay regions are created/removed by the toggle effect
    // below — NOT here — so toggling them doesn't rebuild the chart.)

    // Store chart methods
    chartsRef.current.fitContent = () => {
      try {
        chart.timeScale().fitContent();
      } catch (error) {
        console.warn('Error fitting content:', error);
      }
    };

    chartsRef.current.focusRecent = (candles = 100) => {
      try {
        if (candleDataRef.length > 0) {
          const totalCandles = candleDataRef.length;
          const candlesToShow = Math.min(candles, totalCandles);
          const startIndex = Math.max(0, totalCandles - candlesToShow);
          const endIndex = totalCandles - 1;
          
          chart.timeScale().setVisibleLogicalRange({
            from: startIndex,
            to: endIndex
          });
          
          if (DEBUG) {
            console.log(`📊 Focused on last ${candlesToShow} candles`);
          }
        }
      } catch (error) {
        console.warn('Error focusing on recent candles:', error);
      }
    };

    // Handle resize — track BOTH width and height so the chart fills its column
    // (the column height changes as the seasonal/regime rails load in). Only
    // apply when a dimension actually changed, to avoid redundant work.
    let lastW = 0;
    let lastH = 0;
    const handleResize = () => {
      if (!chartsRef.current.main) return;
      const c = containerRef.current;
      if (!c) return;
      const w = c.clientWidth;
      const h = c.clientHeight || height;
      if (w === lastW && h === lastH) return;
      lastW = w; lastH = h;
      chartsRef.current.main.applyOptions({ width: w, height: h });
    };

    window.addEventListener('resize', handleResize);
    // ResizeObserver: the container grows when the taller side rail finishes
    // loading — window 'resize' alone wouldn't catch that. Defer to the next
    // frame so applying the size can't re-enter the observer synchronously
    // ("ResizeObserver loop completed with undelivered notifications").
    let resizeObserver = null;
    let rafId = 0;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(() => { rafId = 0; handleResize(); });
      });
      resizeObserver.observe(container);
    }

    // Set initialized state
    setTimeout(() => {
      setIsLoading(false);
      setIsInitialized(true);
    }, 50);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
      if (chartsRef.current.main) {
        try {
          chartsRef.current.main.remove();
        } catch (error) {
          console.warn('Error removing chart:', error);
        }
      }
      chartsRef.current = {};
      seriesRef.current = {};
      // A rebuild (pane toggle, symbol change) must re-arm every dependent
      // effect: without flipping isInitialized back, the false->true
      // transition never happens again and candles/markers/matching/pair
      // series are never re-attached to the fresh chart (signals vanished
      // when the MACD/RSI panes were toggled). Clearing the analysis bar key
      // forces the signal pipeline to re-run and repopulate the markers.
      setIsInitialized(false);
      lastAnalysisBarKeyRef.current = null;
    };
  }, [
    // Deliberately NOT including hasMarketData: this effect creates the chart shell,
    // which doesn't need candle data to exist. Including it caused the whole chart
    // (and both marker primitives) to be torn down and rebuilt on every data
    // reload — not just real symbol/layout changes — since hasMarketData goes
    // false at the start of every fetch (see App.js's loadData).
    // effectiveShowRSI / effectiveShowMACD deliberately OMITTED: toggling MACD/RSI
    // must NOT rebuild the chart (a rebuild flips isInitialized and refetches the
    // signals + matching line). They're added/removed by the toggle effect below.
    showVolume,
    showSuperTrend,
    height,
    symbol,
    handleCrosshairMove
  ]);

  // Add/remove the MACD & RSI overlay regions WITHOUT rebuilding the chart.
  // Runs on toggle only; the pane layout is recomputed for whatever is enabled.
  // Population of the freshly-created series is handled by the indicator-update
  // effect below (which also re-runs on the config change, in declaration order
  // AFTER this one, so the series already exist when it sets their data).
  useEffect(() => {
    const chart = chartsRef.current.main;
    if (!isInitialized || !chart) return;

    // ---- RSI ----
    if (effectiveShowRSI && !seriesRef.current.rsi) {
      const rsiSeries = chart.addSeries(LineSeries, {
        color: '#9C27B0', lineWidth: 2, title: 'RSI(14)',
        priceScaleId: 'rsi_overlay', priceLineVisible: false,
      });
      rsiSeries.createPriceLine({ price: 70, color: '#ef4444', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '70' });
      rsiSeries.createPriceLine({ price: 30, color: '#10b981', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '30' });
      rsiSeries.createPriceLine({ price: 50, color: '#6b7280', lineWidth: 1, lineStyle: 3, axisLabelVisible: false });
      seriesRef.current.rsi = rsiSeries;
    } else if (!effectiveShowRSI && seriesRef.current.rsi) {
      try { chart.removeSeries(seriesRef.current.rsi); } catch (e) { /* already gone */ }
      seriesRef.current.rsi = null;
    }

    // ---- MACD (line + signal + histogram) ----
    if (effectiveShowMACD && !seriesRef.current.macdLine) {
      const macdLineSeries = chart.addSeries(LineSeries, { color: '#00BCD4', lineWidth: 2, title: 'MACD', priceScaleId: 'macd_overlay', priceLineVisible: false });
      const signalLineSeries = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 2, title: 'Signal', priceScaleId: 'macd_overlay', priceLineVisible: false });
      const histogramSeries = chart.addSeries(HistogramSeries, { color: '#4CAF50', title: 'Histogram', priceScaleId: 'macd_overlay', priceLineVisible: false, lastValueVisible: false });
      macdLineSeries.createPriceLine({ price: 0, color: '#6b7280', lineWidth: 1, lineStyle: 3, axisLabelVisible: false });
      seriesRef.current.macdLine = macdLineSeries;
      seriesRef.current.signalLine = signalLineSeries;
      seriesRef.current.histogram = histogramSeries;
    } else if (!effectiveShowMACD && seriesRef.current.macdLine) {
      [seriesRef.current.macdLine, seriesRef.current.signalLine, seriesRef.current.histogram]
        .forEach((s) => { if (s) { try { chart.removeSeries(s); } catch (e) { /* already gone */ } } });
      seriesRef.current.macdLine = null;
      seriesRef.current.signalLine = null;
      seriesRef.current.histogram = null;
    }

    // ---- re-apply the pane layout for the current enabled set ----
    const m = computePaneMargins({ showVolume, showRSI: effectiveShowRSI, showMACD: effectiveShowMACD });
    chart.priceScale('right').applyOptions({ scaleMargins: m.price });
    if (seriesRef.current.volume) seriesRef.current.volume.priceScale().applyOptions({ scaleMargins: m.volume });
    if (seriesRef.current.rsi) seriesRef.current.rsi.priceScale().applyOptions({ scaleMargins: m.rsi });
    if (seriesRef.current.macdLine) seriesRef.current.macdLine.priceScale().applyOptions({ scaleMargins: m.macd });
  }, [effectiveShowRSI, effectiveShowMACD, showVolume, isInitialized]);

  // Auth transition: clear the Matching projection on logout; on login,
  // invalidate the analysis cache so signals + the Matching line (re)fetch
  // immediately (the data effect below lists isAuthed, so it re-runs here).
  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthed) {
      seriesRef.current.prediction?.setData([]);
      seriesRef.current.predictionBand?.setData([]);
      seriesRef.current.predictionHigh?.setData([]);
      seriesRef.current.predictionLow?.setData([]);
      setForecastSummary(null);
    } else {
      lastAnalysisBarKeyRef.current = null;
    }
  }, [isAuthed, isInitialized]);

  // REPLACE the existing main data update useEffect with this new version
  useEffect(() => {
    if (!isInitialized || !effectiveMarketData?.candles) return;

    // NOTE on cancellation: this effect re-runs every ~5s, because live WebSocket
    // price ticks give `effectiveMarketData` a fresh reference on each update. A
    // re-run must NOT discard the previous run's in-flight analysis — a tick that
    // merely mutates the current candle produces the same analysisBarKey, so the
    // re-run skips fetching and the in-flight result is the only one coming. Results
    // are therefore discarded solely via the requestId guard (superseded by a NEWER
    // fetch), never via an effect-cleanup "cancelled" flag. requestId is allocated
    // right before a fetch is dispatched, not here, so wasted firings (interval
    // mismatch, unchanged bar key) can't invalidate a real request either.

    if (fullAnalysisTimeoutRef.current) {
      clearTimeout(fullAnalysisTimeoutRef.current);
      fullAnalysisTimeoutRef.current = null;
    }
    if (predictionTimeoutRef.current) {
      clearTimeout(predictionTimeoutRef.current);
      predictionTimeoutRef.current = null;
    }

    const runAnalysis = async () => {
      try {
        // The timeframe prop updates immediately on click, but effectiveMarketData
        // only updates once the parent's fetch for that new timeframe resolves. In
        // between, this effect can fire with *stale* candles (still the old
        // timeframe) mislabeled with the *new* timeframe, which computes/caches a
        // bogus analysis under a wrong key and produces inconsistent
        // "markers appear/disappear on switch" behavior. Skip runs where the data
        // hasn't caught up yet, and clear markers so nothing stale lingers.
        const dataInterval = String(effectiveMarketData.interval || '').toLowerCase();
        const wantedInterval = String(timeframe || '').toLowerCase();
        if (dataInterval && wantedInterval && dataInterval !== wantedInterval) {
          if (seriesRef.current.candlestickMarkers) {
            seriesRef.current.candlestickMarkers.setMarkers([]);
          }
          // Invalidate the bar-key cache so that once matching data arrives, it's
          // guaranteed to look "new" and actually re-run analysis + re-populate
          // markers, instead of possibly matching a stale key (e.g. returning to a
          // timeframe whose latest candle/count is unchanged since we last analyzed
          // it) and silently skipping the fetch that would have refilled them.
          lastAnalysisBarKeyRef.current = null;
          return;
        }

        const candleData = effectiveMarketData.candles.map(c => {
            const timestamp = c.timestamp ? 
              new Date(c.timestamp).getTime() / 1000 : 
              new Date(c.time).getTime() / 1000;
            
            return {
              time: timestamp,
              open: parseFloat(c.open),
              high: parseFloat(c.high),
              low: parseFloat(c.low),
              close: parseFloat(c.close),
              volume: parseFloat(c.volume || 1),
            };
        }).sort((a, b) => a.time - b.time);

        if (candleData.length === 0) return;
        setCandleDataRef(candleData);

        const lastCandle = candleData[candleData.length - 1];
        const analysisBarKey = `${symbol}|${timeframe}|${candleData.length}|${lastCandle?.time}`;
        const shouldRunBackendAnalysis = lastAnalysisBarKeyRef.current !== analysisBarKey;

        // --- CORE LOGIC: Backend Trading Analysis ---
        if (showSuperTrend && shouldRunBackendAnalysis) {
          lastAnalysisBarKeyRef.current = analysisBarKey;

          // Allocate the request id here, right before actually dispatching a fetch,
          // so it only ever supersedes another REAL in-flight request, not a wasted
          // early-exit firing of this effect.
          const requestId = analysisRequestIdRef.current + 1;
          analysisRequestIdRef.current = requestId;

          if (DEBUG) {
            console.log('🔄 Integrated System: Starting analysis...');
          }

          // Matching line: fired IN PARALLEL with the analysis (it used to wait
          // behind the fast-analysis round trip) and with a slim payload — the
          // matcher only needs close + time, not full OHLCV (~60% smaller over
          // the tunnel). GATED: skipped entirely for logged-out visitors (saves
          // the round trip too) — it unlocks on login.
          if (isAuthed && seriesRef.current.prediction) {
            const runPrediction = async (attempt = 0) => {
              try {
                if (analysisRequestIdRef.current !== requestId) return;
                const predictCandles = candleData.slice(-2000)
                  .map(c => ({ time: c.time, close: c.close }));
                const prediction = await fetchPrediction({
                  candles: predictCandles,
                  window: 50,
                  pred_len: 30
                });

                const points = (prediction?.points || [])
                  .filter(p => p.time && Number.isFinite(p.value))
                  .map(p => ({
                    time: p.time,
                    value: p.value
                  }));
                const pointsHigh = (prediction?.points_high || [])
                  .filter(p => p.time && Number.isFinite(p.value))
                  .map(p => ({ time: p.time, value: p.value }));
                const pointsLow = (prediction?.points_low || [])
                  .filter(p => p.time && Number.isFinite(p.value))
                  .map(p => ({ time: p.time, value: p.value }));
                const bandByTime = new Map(pointsHigh.map(p => [p.time, p.value]));
                const bandData = pointsLow
                  .map(p => {
                    const high = bandByTime.get(p.time);
                    if (!Number.isFinite(high)) return null;
                    return { time: p.time, high, low: p.value };
                  })
                  .filter(Boolean);

                if (analysisRequestIdRef.current !== requestId) return;
                if (!seriesRef.current.prediction) return; // chart torn down while fetch was in flight
                setForecastSummary({
                  probUp: prediction?.prob_up,
                  analogCount: prediction?.analogs?.count,
                  matches: prediction?.analogs?.matches || [],
                  calibration: prediction?.calibration || null,
                });
                seriesRef.current.prediction.setData(points);
                if (seriesRef.current.predictionBand) {
                  seriesRef.current.predictionBand.setData(bandData);
                }
                if (seriesRef.current.predictionHigh) {
                  seriesRef.current.predictionHigh.setData(pointsHigh);
                }
                if (seriesRef.current.predictionLow) {
                  seriesRef.current.predictionLow.setData(pointsLow);
                }
              } catch (error) {
                if (DEBUG) {
                  console.warn('⚠️ Prediction failed:', error);
                }
                // A failed fetch (tunnel blip beyond the API layer's retry)
                // would otherwise leave the chart with NO matching line until
                // the next bar. One delayed retry, still superseded by any
                // newer request via the requestId check inside.
                if (attempt < 1) {
                  predictionTimeoutRef.current = setTimeout(() => runPrediction(attempt + 1), 8000);
                }
              }
            };
            predictionTimeoutRef.current = setTimeout(() => runPrediction(), 0);
          }

          const limitedCandles = candleData.slice(-300); // quick first pass

          const fastResult = await fetchTradingAnalysis({
            candles: limitedCandles,
            config: { superTrendConfig: stableSuperTrendConfig }
          });

          if (analysisRequestIdRef.current !== requestId) return;
          if (DEBUG) {
            console.log('✅ Integrated System: Fast analysis complete.', fastResult);
          }
          fastResult._sym = symbol; fastResult._tf = timeframe; // stale-guard tag
          setAnalysisResult(fastResult);
          if (onNewAnalysis) onNewAnalysis(fastResult);

          // Background full analysis (uses more data, updates chart when ready)
          const fullCandles = candleData.slice(-2000); // keep safe for performance
          if (fullCandles.length > limitedCandles.length) {
            fullAnalysisTimeoutRef.current = setTimeout(async () => {
              try {
                if (analysisRequestIdRef.current !== requestId) return;
                const fullResult = await fetchTradingAnalysis({
                  candles: fullCandles,
                  config: { superTrendConfig: stableSuperTrendConfig }
                });

                if (analysisRequestIdRef.current !== requestId) return;
                if (DEBUG) {
                  console.log('✅ Integrated System: Full analysis complete.', fullResult);
                }
                fullResult._sym = symbol; fullResult._tf = timeframe; // stale-guard tag
                setAnalysisResult(fullResult);
                if (onNewAnalysis) onNewAnalysis(fullResult);
              } catch (error) {
                if (DEBUG) {
                  console.warn('⚠️ Background full analysis failed:', error);
                }
              }
            }, 0);
          }

        }
        // --- END CORE LOGIC ---

        // The rest of the indicator logic (RSI, MACD, etc.) can remain
        // as it is, since it's separate from the SuperTrend analysis.
        if (seriesRef.current.candlestick) {
          seriesRef.current.candlestick.setData(candleData);
        }

        // Set volume data
        if (seriesRef.current.volume) {
          const volumeData = candleData.map(candle => ({
            time: candle.time,
            value: candle.volume,
            color: candle.close >= candle.open ? 
              'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
          }));
          seriesRef.current.volume.setData(volumeData);
        }

        // Computed indicators (SMA/EMA/BB/VWAP/RSI/MACD) are owned by the
        // indicator-update effect, which re-fires off the setCandleDataRef call
        // above — this effect only pushes raw data and runs the backend analysis.

        if (isFirstLoad) {
          if (chartsRef.current.focusRecent) {
            chartsRef.current.focusRecent(150);
          }
          setIsFirstLoad(false);
        }
        
      } catch (error) {
        console.error('❌ Error running integrated analysis:', error);
        // The bar key was optimistically marked as analyzed before the fetch; undo
        // that on failure so the next pass (e.g. the next price tick) retries
        // instead of permanently skipping this dataset.
        lastAnalysisBarKeyRef.current = null;
      }
    };

    runAnalysis();

    return () => {
      if (fullAnalysisTimeoutRef.current) {
        clearTimeout(fullAnalysisTimeoutRef.current);
        fullAnalysisTimeoutRef.current = null;
      }
      if (predictionTimeoutRef.current) {
        clearTimeout(predictionTimeoutRef.current);
        predictionTimeoutRef.current = null;
      }
    };
  }, [isInitialized, effectiveMarketData, showSuperTrend, onNewAnalysis, timeframe, isAuthed]); // isAuthed: re-run to (un)fetch signals + Matching on login/logout

  // (Pair-trading spread now has its own dedicated chart inside
  // PairTradingSection — two rebased price lines + z-score with signals — so
  // it no longer adds a pane to the main candlestick chart.)

  // ADD THIS ENTIRE NEW useEffect HOOK
  useEffect(() => {
    if (!analysisResult || !chartsRef.current.main) return;
    // Stale guard: an analysis result carries the symbol/timeframe it was
    // computed for. After a switch, the previous symbol's result is still in
    // state (its fetch may even land late) — skip painting it so the old
    // SuperTrend/ION line can't blow out the new symbol's price scale.
    if ((analysisResult._sym && analysisResult._sym !== symbol) ||
        (analysisResult._tf && analysisResult._tf !== timeframe)) {
      return;
    }

    const chart = chartsRef.current.main;

    // --- START: ROBUST MARKER AND VISUALIZATION LOGIC ---

    // 1. Set Regime Background Color
    if (showRegimeBackground) {
      const regimeColor = analysisResult.regimeVisual?.backgroundColor || 'rgba(255, 255, 255, 0)';
      chart.applyOptions({
        layout: {
          background: { type: 'solid', color: regimeColor },
        },
      });
    } else {
      chart.applyOptions({
        layout: {
          background: { type: 'solid', color: '#ffffff' },
        },
      });
    }

    // 2. Update the SuperTrend line
    if (seriesRef.current.superTrend && analysisResult.superTrend.singleLine) {
        const superTrendLineData = analysisResult.superTrend.singleLine.map((point, index) => {
            const isBullish = analysisResult.superTrend.trend[index]?.value === 1;
            return {
                time: point.time,
                value: point.value,
                color: isBullish ? '#26a69a' : '#ef5350'
            };
        }).filter(p => p.value !== null);
        seriesRef.current.superTrend.setData(superTrendLineData);
    }

    // 2b. Market-structure EMAs (also analysis-derived, only when enabled)
    const stFilters = analysisResult.debug?.superTrendRaw?.filters;
    if (stableSuperTrendConfig.useMarketStructure && stFilters) {
        const setEmaLine = (values, series) => {
            if (!values || !series) return;
            const lineData = values.map((value, index) => ({
                time: candleDataRef[index]?.time,
                value: value
            })).filter(point => point.value !== undefined && point.time);
            series.setData(lineData);
        };
        setEmaLine(stFilters.emaFast, seriesRef.current.superTrendEmaFast);
        setEmaLine(stFilters.emaSlow, seriesRef.current.superTrendEmaSlow);
        setEmaLine(stFilters.emaBaseline, seriesRef.current.superTrendEmaBaseline);
    }

    // 2c. Base-after-decline watch: draw the breakout trigger as a dashed
    // price line so the user can monitor the setup before any entry exists.
    if (seriesRef.current.candlestick) {
      if (seriesRef.current.baseTriggerLine) {
        try { seriesRef.current.candlestick.removePriceLine(seriesRef.current.baseTriggerLine); } catch (e) { /* gone */ }
        seriesRef.current.baseTriggerLine = null;
      }
      const bw = analysisResult.baseWatch;
      if (bw?.active && Number.isFinite(bw.baseHigh)) {
        seriesRef.current.baseTriggerLine = seriesRef.current.candlestick.createPriceLine({
          price: bw.baseHigh,
          color: '#7c3aed',
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: 'base trigger',
        });
      }
    }

    // 3. Build and apply entry/TP/SL markers (pure logic lives in utils/analysisMarkers).
    // GATED: logged-out visitors keep the SuperTrend/ION line (set above) but
    // don't get the entry/TP/SL signal markers — those unlock on login.
    if (!isAuthed) {
      if (seriesRef.current.candlestickMarkers) {
        seriesRef.current.candlestickMarkers.setMarkers([]);
      }
      seriesRef.current.currentMarkers = [];
      return;
    }

    const allMarkers = buildAnalysisMarkers(analysisResult);
    if (!allMarkers) {
        console.warn("Position data not found in analysis result. Cannot draw markers.");
        seriesRef.current.candlestickMarkers.setMarkers([]);
        seriesRef.current.currentMarkers = [];
        return;
    }

    if (seriesRef.current.candlestickMarkers) {
      seriesRef.current.candlestickMarkers.setMarkers(allMarkers);
    }
    seriesRef.current.currentMarkers = allMarkers;
    if (DEBUG) {
      console.log(`✅ Applied ${allMarkers.length} total markers using robust position IDs.`);
    }

  }, [analysisResult, showRegimeBackground, isInitialized, isAuthed, symbol, timeframe]); // re-runs after chart rebuilds (pane toggles) so markers survive

  // Swing volume profile (support/resistance + PoC) — toggleable, default off
  const showSwingProfile = currentIndicatorConfig?.srProfile?.enabled || false;
  useEffect(() => {
    const primitive = seriesRef.current.swingProfile;
    if (!primitive) return;
    primitive.updateData(
      showSwingProfile && candleDataRef.length > 0
        ? computeSwingProfiles(candleDataRef)
        : []
    );
  }, [candleDataRef, showSwingProfile, isInitialized]);

  // Swing high/low + candlestick pattern labels — toggleable from the pill bar
  const showSwingPatterns = currentIndicatorConfig?.swingPatterns?.enabled ?? true;
  useEffect(() => {
    if (!seriesRef.current.swingPatternMarkers) return;

    if (!showSwingPatterns || candleDataRef.length === 0) {
      seriesRef.current.swingPatternMarkers.setMarkers([]);
      return;
    }

    const swingPoints = detectSwingPatterns(candleDataRef, 21);
    const markers = swingPoints.map((point) => ({
      time: point.time,
      position: point.type === 'high' ? 'aboveBar' : 'belowBar',
      color: point.type === 'high' ? '#ef4444' : '#14b8a6',
      shape: point.type === 'high' ? 'arrowDown' : 'arrowUp',
      text: point.pattern ? `${point.label} ${point.pattern.title}` : point.label,
      size: 1,
      id: `swing_${point.type}_${point.time}`,
    }));
    markers.sort((a, b) => a.time - b.time);

    seriesRef.current.swingPatternMarkers.setMarkers(markers);
  }, [candleDataRef, showSwingPatterns, isInitialized]);

  // FIXED: Update indicator data when config changes
  useEffect(() => {
    if (!isInitialized) return;
    
    const updateIndicatorData = () => {
      try {
        const config = getIndicatorConfig();
        const indicators = {};

        // Calculate SMA
        if (seriesRef.current.smaLines && config.sma?.enabled && config.sma?.periods) {
          config.sma.periods.forEach(period => {
            if (seriesRef.current.smaLines[period] && candleDataRef.length >= period) {
              const smaData = TechnicalIndicatorsService.calculateSMA(candleDataRef, period);
              if (smaData.length > 0) {
                seriesRef.current.smaLines[period].setData(smaData);
                indicators[`sma${period}`] = smaData;
              }
            }
          });
        }

        // Calculate EMA
        if (seriesRef.current.emaLines && config.ema?.enabled && config.ema?.periods) {
          config.ema.periods.forEach(period => {
            if (seriesRef.current.emaLines[period] && candleDataRef.length >= period) {
              const emaData = TechnicalIndicatorsService.calculateEMA(candleDataRef, period);
              if (emaData.length > 0) {
                seriesRef.current.emaLines[period].setData(emaData);
                indicators[`ema${period}`] = emaData;
              }
            }
          });
        }

        // Calculate Bollinger Bands
        if (seriesRef.current.bollingerBands.upper && config.bollingerBands?.enabled) {
          const period = config.bollingerBands.period || 20;
          const stdDev = config.bollingerBands.stdDev || 2;
          
          if (candleDataRef.length >= period) {
            const bbData = TechnicalIndicatorsService.calculateBollingerBands(candleDataRef, { period, stdDev });
            if (bbData.length > 0) {
              const upperData = bbData.map(item => ({ time: item.time, value: item.upper }));
              const middleData = bbData.map(item => ({ time: item.time, value: item.middle }));
              const lowerData = bbData.map(item => ({ time: item.time, value: item.lower }));
              
              seriesRef.current.bollingerBands.upper.setData(upperData);
              seriesRef.current.bollingerBands.middle.setData(middleData);
              seriesRef.current.bollingerBands.lower.setData(lowerData);
              indicators.bollingerBands = bbData;
            }
          }
        }

        // Calculate VWAP
        if (seriesRef.current.vwap && config.vwap?.enabled) {
          const vwapData = TechnicalIndicatorsService.calculateVWAP(candleDataRef);
          if (vwapData.length > 0) {
            seriesRef.current.vwap.setData(vwapData);
            indicators.vwap = vwapData;
          }
        }

        // Calculate and set RSI
        if (seriesRef.current.rsi && candleDataRef.length >= 15) {
          const rsiData = TechnicalIndicatorsService.calculateRSI(candleDataRef, 14);
          if (rsiData.length > 0) {
            seriesRef.current.rsi.setData(rsiData);
            indicators.rsi = rsiData;
          }
        }

        // Calculate and set MACD
        if (seriesRef.current.macdLine && candleDataRef.length >= 26) {
          const macdData = TechnicalIndicatorsService.calculateMACD(candleDataRef, {
            fastPeriod: 12,
            slowPeriod: 26,
            signalPeriod: 9
          });
          
          if (macdData.length > 0) {
            indicators.macd = macdData;
            
            const macdLineData = macdData.map(item => ({
              time: item.time,
              value: item.MACD
            }));
            seriesRef.current.macdLine.setData(macdLineData);

            const signalLineData = macdData.map(item => ({
              time: item.time,
              value: item.signal
            }));
            seriesRef.current.signalLine.setData(signalLineData);

            const histogramData = macdData.map(item => ({
              time: item.time,
              value: item.histogram,
              color: item.histogram >= 0 ? '#4CAF50' : '#F44336',
            }));
            seriesRef.current.histogram.setData(histogramData);
          }
        }

        // SuperTrend line and market-structure EMAs are analysis-derived, so they
        // are owned by the marker/visualization effect keyed on analysisResult —
        // reading analysisResult here would be a stale closure (it isn't a dep).

      } catch (error) {
        console.error('Error updating indicator data:', error);
      }
    };
    
    updateIndicatorData();
  }, [isInitialized, currentIndicatorConfig, candleDataRef, showSuperTrend, stableSuperTrendConfig]);

  const handleTimeframeSelect = (nextTimeframe) => {
    if (DEBUG) {
      console.log(`Timeframe selected: ${nextTimeframe}`);
    }
    onTimeframeChange(nextTimeframe);
  };


  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full">
      {/* Chart toolbar — a real bar above the chart (not floated over it), so
          on phone the pills wrap into clean rows instead of covering candles. */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100">
        <TimeframeControls
          selectedTimeframe={timeframe}
          onSelectTimeframe={handleTimeframeSelect}
        />
        <IndicatorControls config={currentIndicatorConfig} onConfigChange={handleIndicatorConfigChange} isAuthed={isAuthed} onRequestLogin={onRequestLogin} />
        {isAuthed && typeof forecastSummary?.probUp === 'number' && forecastSummary.matches?.length > 0 && (() => {
          const cal = forecastSummary.calibration;
          const toSec = (t) => {
            if (typeof t === 'number') return t > 1e12 ? Math.floor(t / 1000) : t;
            const ms = new Date(t).getTime();
            return Number.isFinite(ms) ? Math.floor(ms / 1000) : NaN;
          };
          const jumpToMatch = () => {
            const match = forecastSummary.matches?.[0];
            const chart = chartsRef.current.main;
            if (!match || !chart) return;
            const from = toSec(match.startTime);
            const to = toSec(match.futureEndTime || match.endTime);
            if (!Number.isFinite(from) || !Number.isFinite(to)) return;
            const pad = Math.max(1, (to - from) * 0.2);
            chart.timeScale().setVisibleRange({ from: from - pad, to: to + pad });
            const simText = typeof match.similarity === 'number'
              ? ` (${Math.round(match.similarity * 100)}% similar)` : '';
            seriesRef.current.matchMarkers?.setMarkers([
              { time: toSec(match.startTime), position: 'belowBar', color: '#9333ea',
                shape: 'arrowUp', text: `match start${simText}` },
              { time: toSec(match.endTime), position: 'aboveBar', color: '#9333ea',
                shape: 'arrowDown', text: 'match end → what followed drives the projection' },
            ]);
            setViewingMatch(true);
          };
          const backToNow = () => {
            seriesRef.current.matchMarkers?.setMarkers([]);
            chartsRef.current.main?.timeScale().scrollToRealTime();
            setViewingMatch(false);
          };
          return (
            <button
              onClick={viewingMatch ? backToNow : jumpToMatch}
              className="ml-auto text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1 border border-gray-200 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
              title={`The Matching line replays the ${forecastSummary.analogCount || '–'} historical episodes most similar to now (50-bar shape + 250-bar context): median path as the line, 10–90% of their outcomes as the band.${cal ? ` Band width backtested on ${cal.anchors} recent replays: ${Math.round(cal.coverage * 100)}% coverage${cal.widen > 1 ? ` after ${cal.widen}x widening` : ''}.` : ''} Click to ${viewingMatch ? 'return to the present.' : 'view the best-matching episode on the chart.'}`}
            >
              {viewingMatch ? '← back to now' : (
                <>
                  {typeof forecastSummary.matches[0].similarity === 'number' &&
                    <span className="text-gray-400">match {Math.round(forecastSummary.matches[0].similarity * 100)}% · </span>}
                  <span className={forecastSummary.probUp >= 0.5 ? 'text-green-600' : 'text-red-600'}>
                    ▲ {Math.round(forecastSummary.probUp * 100)}%
                  </span>
                  <span className="text-gray-400"> view →</span>
                </>
              )}
            </button>
          );
        })()}
      </div>

      {/* Honest strategy record + base watch, right under the toolbar */}
      {(analysisResult?.strategyStats || analysisResult?.baseWatch?.active) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5 border-b border-gray-100 text-[11px] text-gray-600">
          {analysisResult?.strategyStats && (() => {
            const h = analysisResult.strategyStats.holdout || {};
            const o = analysisResult.strategyStats.overall || {};
            const a = analysisResult.strategyStats.assumptions || {};
            const fmt = (v, suf = '') => (v === null || v === undefined ? '—' : `${v}${suf}`);
            return (
              <span className="flex items-center gap-1">
                <span className="font-medium text-gray-700">ION record (full history):</span>
                <span>{fmt(o.trades)} trades</span>·<span>win {fmt(o.winRate, '%')}</span>·
                <span>Sharpe {fmt(o.sharpe)}</span>·
                <span className={o.totalPnlPct > 0 ? 'text-green-600' : 'text-red-600'}>
                  PnL {o.totalPnlPct > 0 ? '+' : ''}{fmt(o.totalPnlPct, '%')}
                </span>·
                <span className="text-red-500">maxDD {fmt(o.maxDrawdownPct, '%')}</span>
                {h.trades > 0 && (
                  <span className="text-gray-400">
                    · OOS {h.bars || 250}b: {fmt(h.trades)}t / win {fmt(h.winRate, '%')} / PnL{' '}
                    {h.totalPnlPct > 0 ? '+' : ''}{fmt(h.totalPnlPct, '%')}
                  </span>
                )}
                <InfoHint title="Honest strategy record" align="left">
                  BUY-side SuperTrend flips on this symbol/timeframe, fills at the NEXT
                  bar's open, no fees. Headline is the full history: {fmt(o.trades)} trades,
                  win {fmt(o.winRate, '%')}, Sharpe {fmt(o.sharpe)}, PnL{' '}
                  {o.totalPnlPct > 0 ? '+' : ''}{fmt(o.totalPnlPct, '%')}, maxDD {fmt(o.maxDrawdownPct, '%')},
                  PF {fmt(o.profitFactor)}. The OOS tag is the last {h.bars || 250} bars only
                  (the model never trained on them) — {fmt(h.trades)} trades, so it's a small
                  honesty check, not the headline. {a.side}. {a.annualization}.
                  Past performance is not a promise.
                </InfoHint>
              </span>
            );
          })()}
          {analysisResult?.baseWatch?.active && (() => {
            const bw = analysisResult.baseWatch;
            const label = bw.status === 'FORMING' ? 'Base forming'
              : bw.status === 'BREAKOUT' ? 'BASE BREAKOUT'
              : 'Base breakout (weak volume)';
            const cls = bw.status === 'BREAKOUT'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-violet-50 text-violet-700 border-violet-200';
            return (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${cls}`}
                title={bw.note}>
                {label} · {bw.baseBars} bars after −{bw.declinePct}% · trigger &gt; {bw.baseHigh}
                {bw.volumeContracting ? ' · vol drying up' : ''}
              </span>
            );
          })()}
        </div>
      )}
      {/* flex-1 so the chart grows to fill the column height on wide screens
          (matching the seasonal/regime rails — no empty gap under it); the
          minHeight keeps it tall on stacked/phone layouts. */}
      <div className="relative flex-1" style={{ minHeight: height }}>
        <div ref={containerRef} className="w-full h-full" />
        {!hasMarketData && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10 text-gray-500">
            No market data available.
          </div>
        )}
        {hasMarketData && isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <div className="text-lg font-semibold">Loading Chart...</div>
          </div>
        )}
        {/* MODIFIED Tooltip with separate logic for Entries and Exits */}
        {markerTooltip && (
          <div
            className="absolute z-50 bg-white text-gray-900 border border-gray-200 px-3 py-2 rounded shadow-lg text-xs pointer-events-none w-64"
            style={{ left: tooltipPosition.x, top: tooltipPosition.y }}
          >
            {/* ---- LOGIC FOR AN EXIT MARKER (TP or SL) ---- */}
            {markerTooltip.data.exitType ? (
              (() => {
                // --- PnL Calculation ---
                const entryPrice = markerTooltip.data.baseSignal?.price;
                const exitPrice = markerTooltip.data.exitPrice;
                const direction = markerTooltip.data.baseSignal?.direction;
                let pnl = 0;
                let pnlPct = 0;
                let isProfit = false;

                if (entryPrice && exitPrice && direction) {
                    if (direction === 'BUY') {
                        pnl = exitPrice - entryPrice;
                    } else { // SELL
                        pnl = entryPrice - exitPrice;
                    }
                    pnlPct = (pnl / entryPrice) * 100;
                    isProfit = pnl > 0;
                }

                return (
                  <div>
                    <div className="font-bold text-base mb-2">
                      Trade Exit: <span style={{color: markerTooltip.color}}>{markerTooltip.data.exitType}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {/* PnL: close of the signal candle -> close of the TP/SL trigger candle */}
                      <strong>PnL:</strong>
                      <span className={isProfit ? 'text-green-600' : 'text-red-600'}>
                        {isProfit ? '+' : ''}${pnl.toFixed(2)} ({pnlPct.toFixed(2)}%)
                      </span>

                      <strong>Exit Price:</strong>
                      <span>${exitPrice?.toFixed(2)}</span>

                      {/* --- UPDATED: Entry Price --- */}
                      <strong>Entry Price:</strong>
                      <span>${entryPrice?.toFixed(2)} ({direction})</span>

                      <strong>Entry Grade:</strong>
                      <span>{markerTooltip.data.strengthGrade} ({markerTooltip.data.finalScore})</span>
                    </div>

                    {/* --- UPDATED: Reason for Exit --- */}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="font-semibold">Reason for Exit:</div>
                      <div className="text-gray-500 italic">
                        {markerTooltip.data.exitReason}
                      </div>
                    </div>
                  </div>
                )
              })()
            ) : (
              /* ---- LOGIC FOR AN ENTRY SIGNAL MARKER (The original tooltip) ---- */
              <div>
                <div className="font-bold text-base mb-2">
                  Signal Grade: <span style={{color: markerTooltip.color}}>{markerTooltip.data.strengthGrade}</span> ({markerTooltip.data.finalScore})
                </div>
                <div className="text-xs">
                  <div><strong>Regime:</strong> {markerTooltip.data.regimeContext.regime}</div>
                  {markerTooltip.data.regimeIntel && (
                    <div>
                      <strong>{markerTooltip.data.regimeIntel.mode === 'proxy' ? 'Regime proxy (OOS):' : 'Regime model (OOS):'}</strong>{' '}
                      {markerTooltip.data.regimeIntel.label}
                      {markerTooltip.data.regimeIntel.probabilities &&
                        ` ${Math.round(markerTooltip.data.regimeIntel.probabilities.bull * 100)}% bull`}
                      {' · '}
                      <span className={markerTooltip.data.regimeIntel.adjustment >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {markerTooltip.data.regimeIntel.adjustment >= 0 ? '+' : ''}
                        {markerTooltip.data.regimeIntel.adjustment} pts
                      </span>
                    </div>
                  )}
                  <div><strong>Direction:</strong> {markerTooltip.data.baseSignal.direction}</div>
                  <div><strong>Price:</strong> ${markerTooltip.data.baseSignal.price.toFixed(2)}</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="font-semibold">Confirmations:</div>
                  {markerTooltip.data.confirmations.length > 0 ? (
                    <ul className="list-disc list-inside text-green-600">
                      {markerTooltip.data.confirmations.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  ) : <span className="text-gray-500">None</span>}
                </div>
                <div className="mt-2">
                  <div className="font-semibold">Warnings:</div>
                  {markerTooltip.data.warnings.length > 0 ? (
                    <ul className="list-disc list-inside text-amber-600">
                      {markerTooltip.data.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  ) : <span className="text-gray-500">None</span>}
                </div>
              </div>
            )}
            {tooltipPinnedRef.current && (
              <div className="mt-2 pt-1.5 border-t border-gray-100 text-[10px] text-gray-400">
                Tap an empty area of the chart to close
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiPaneChart;
